import { type QueryClient } from '@tanstack/react-query'

import type { Evaluation } from '@/types/assessment_process.types'

export interface EvaluationQueryKeyFactory {
  evaluations: {
    all: () => readonly ['evaluations']
    lists: () => readonly ['evaluations', 'list']
    list: (params?: {
      assessment_id: string
      formato?: string
      sin_nivel_actual?: boolean
      sin_nivel_objetivo?: boolean
      con_brecha?: boolean
      codigo_pattern?: string
    }) => readonly [
      'evaluations',
      'list',
      (
        | {
            assessment_id: string
            formato?: string
            sin_nivel_actual?: boolean
            sin_nivel_objetivo?: boolean
            con_brecha?: boolean
            codigo_pattern?: string
          }
        | undefined
      )
    ]
    detail: (
      assessmentId: string,
      evaluationId: string
    ) => readonly ['evaluations', 'detail', string, string]
    byAssessment: (assessmentId: string) => readonly ['evaluations', 'assessment', string]
  }
}

export const evaluationQueryKeys: EvaluationQueryKeyFactory = {
  evaluations: {
    all: () => ['evaluations'] as const,
    lists: () => ['evaluations', 'list'] as const,
    list: (params) => ['evaluations', 'list', params] as const,
    detail: (assessmentId, evaluationId) =>
      ['evaluations', 'detail', assessmentId, evaluationId] as const,
    byAssessment: (assessmentId) => ['evaluations', 'assessment', assessmentId] as const
  }
}

export interface EvaluationQueryUtils {
  invalidateEvaluationQueries: (queryClient: QueryClient, assessmentId?: string) => Promise<void>
  setEvaluationOptimistic: (
    queryClient: QueryClient,
    assessmentId: string,
    evaluationId: string,
    data: Partial<Evaluation>
  ) => void
  removeEvaluationFromCache: (
    queryClient: QueryClient,
    assessmentId: string,
    evaluationId: string
  ) => void
}

export const evaluationQueryUtils: EvaluationQueryUtils = {
  async invalidateEvaluationQueries(queryClient, assessmentId) {
    const promises = [
      queryClient.invalidateQueries({ queryKey: evaluationQueryKeys.evaluations.lists() })
    ]

    if (assessmentId) {
      promises.push(
        queryClient.invalidateQueries({
          queryKey: evaluationQueryKeys.evaluations.byAssessment(assessmentId)
        })
      )
    }

    await Promise.all(promises)
  },

  setEvaluationOptimistic(queryClient, assessmentId, evaluationId, data) {
    // Update evaluation detail cache
    queryClient.setQueryData(
      evaluationQueryKeys.evaluations.detail(assessmentId, evaluationId),
      (old: Evaluation | undefined) => {
        if (!old) return old
        return { ...old, ...data }
      }
    )

    // Update evaluation in assessment evaluations list cache
    queryClient.setQueriesData(
      { queryKey: evaluationQueryKeys.evaluations.byAssessment(assessmentId), exact: true },
      (old: Evaluation[] | undefined) => {
        if (!old) return old

        return old.map((evaluation: Evaluation) =>
          evaluation.id === evaluationId ? { ...evaluation, ...data } : evaluation
        )
      }
    )

    // Update evaluation in all lists cache
    queryClient.setQueriesData(
      { queryKey: evaluationQueryKeys.evaluations.lists(), exact: false },
      (old: Evaluation[] | undefined) => {
        if (!old) return old

        return old.map((evaluation: Evaluation) =>
          evaluation.id === evaluationId ? { ...evaluation, ...data } : evaluation
        )
      }
    )
  },

  removeEvaluationFromCache(queryClient, assessmentId, evaluationId) {
    // Remove from detail cache
    queryClient.removeQueries({
      queryKey: evaluationQueryKeys.evaluations.detail(assessmentId, evaluationId)
    })

    // Remove from assessment evaluations cache
    queryClient.setQueryData(
      evaluationQueryKeys.evaluations.byAssessment(assessmentId),
      (old: Evaluation[] | undefined) => {
        if (!old) return old
        return old.filter((evaluation: Evaluation) => evaluation.id !== evaluationId)
      }
    )

    // Remove from lists cache
    queryClient.setQueriesData(
      { queryKey: evaluationQueryKeys.evaluations.lists(), exact: false },
      (old: Evaluation[] | undefined) => {
        if (!old) return old
        return old.filter((evaluation: Evaluation) => evaluation.id !== evaluationId)
      }
    )
  }
}
